'use client';

import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type PointerEvent as ReactPointerEvent,
} from 'react';

import {
  type DownloadManagerDockBounds,
  type DownloadManagerDockSafeInsets,
} from '@/lib/download-manager-docking';
import {
  clampFloatingVoiceDockPoint,
  getFloatingVoiceDockDefaultPosition,
  getFloatingVoiceDockTarget,
  type FloatingVoiceDockPosition,
} from '@/lib/floating-voice-dock-state';
import { observeDownloadManagerGeometry } from '@/lib/observe-download-manager-geometry';

const DRAG_THRESHOLD = 6;

function getSafeInsets(): DownloadManagerDockSafeInsets {
  const probe = document.createElement('div');
  probe.style.cssText =
    'position:fixed;visibility:hidden;pointer-events:none;' +
    'padding:env(safe-area-inset-top) env(safe-area-inset-right) env(safe-area-inset-bottom) env(safe-area-inset-left)';
  document.body.append(probe);
  const style = window.getComputedStyle(probe);
  const insets = {
    top: Number.parseFloat(style.paddingTop) || 0,
    right: Number.parseFloat(style.paddingRight) || 0,
    bottom: Number.parseFloat(style.paddingBottom) || 0,
    left: Number.parseFloat(style.paddingLeft) || 0,
  };
  probe.remove();
  return insets;
}

function getDownloadManagerBounds(): DownloadManagerDockBounds | null {
  const element = document.querySelector(
    '[data-slot="download-manager-boundary"] [data-dock-corner]'
  );
  return element?.getBoundingClientRect() ?? null;
}

export function useFloatingVoiceDockPosition(isSourceChatVisible: boolean) {
  const boundaryRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLElement>(null);
  const defaultPosition =
    getFloatingVoiceDockDefaultPosition(isSourceChatVisible);
  const [position, setPosition] = useState<
    FloatingVoiceDockPosition | 'custom'
  >(defaultPosition);
  const positionRef = useRef<FloatingVoiceDockPosition | 'custom'>(
    defaultPosition
  );
  const [point, setPoint] = useState({ x: 0, y: 0 });
  const [ready, setReady] = useState(false);
  const [dragging, setDragging] = useState(false);
  const pointerRef = useRef<{
    id: number;
    startX: number;
    startY: number;
    originX: number;
    originY: number;
    moved: boolean;
  } | null>(null);
  const suppressClickRef = useRef(false);

  const dock = useCallback((preferred: FloatingVoiceDockPosition) => {
    const panel = panelRef.current;
    const boundary = boundaryRef.current;
    if (!panel || !boundary) return;
    const target = getFloatingVoiceDockTarget(preferred, {
      boundary: boundary.getBoundingClientRect(),
      panel: { width: panel.offsetWidth, height: panel.offsetHeight },
      obstacle: getDownloadManagerBounds(),
      safeInsets: getSafeInsets(),
    });
    setPoint((current) =>
      current.x === target.x && current.y === target.y
        ? current
        : { x: target.x, y: target.y }
    );
    setReady(true);
  }, []);

  useEffect(() => {
    const frame = window.requestAnimationFrame(() => {
      positionRef.current = defaultPosition;
      setPosition(defaultPosition);
      dock(defaultPosition);
    });
    return () => window.cancelAnimationFrame(frame);
  }, [defaultPosition, dock]);

  useEffect(() => {
    const panel = panelRef.current;
    const boundary = boundaryRef.current;
    if (!panel || !boundary) return;
    const update = () => {
      if (pointerRef.current) return;
      const currentPosition = positionRef.current;
      if (currentPosition === 'custom') {
        setPoint((current) =>
          clampFloatingVoiceDockPoint(
            current,
            {
              width: boundary.clientWidth,
              height: boundary.clientHeight,
            },
            { width: panel.offsetWidth, height: panel.offsetHeight }
          )
        );
      } else {
        dock(currentPosition);
      }
    };
    const observer = new ResizeObserver(update);
    observer.observe(panel);
    observer.observe(boundary);
    const stopObservingDownloads = observeDownloadManagerGeometry(update);
    window.addEventListener('resize', update);
    window.addEventListener('orientationchange', update);
    return () => {
      observer.disconnect();
      stopObservingDownloads();
      window.removeEventListener('resize', update);
      window.removeEventListener('orientationchange', update);
    };
  }, [dock]);

  const onDragPointerDown = useCallback(
    (event: ReactPointerEvent<HTMLButtonElement>) => {
      if (event.button !== 0) return;
      pointerRef.current = {
        id: event.pointerId,
        startX: event.clientX,
        startY: event.clientY,
        originX: point.x,
        originY: point.y,
        moved: false,
      };
      event.currentTarget.setPointerCapture(event.pointerId);
    },
    [point.x, point.y]
  );

  const onDragPointerMove = useCallback(
    (event: ReactPointerEvent<HTMLButtonElement>) => {
      const pointer = pointerRef.current;
      if (!pointer || pointer.id !== event.pointerId) return;
      const dx = event.clientX - pointer.startX;
      const dy = event.clientY - pointer.startY;
      if (!pointer.moved && Math.hypot(dx, dy) < DRAG_THRESHOLD) return;
      pointer.moved = true;
      setDragging(true);
      const boundary = boundaryRef.current;
      const panel = panelRef.current;
      if (!boundary || !panel) return;
      setPoint(
        clampFloatingVoiceDockPoint(
          { x: pointer.originX + dx, y: pointer.originY + dy },
          { width: boundary.clientWidth, height: boundary.clientHeight },
          { width: panel.offsetWidth, height: panel.offsetHeight }
        )
      );
    },
    []
  );

  const onDragPointerUp = useCallback(
    (event: ReactPointerEvent<HTMLButtonElement>) => {
      const pointer = pointerRef.current;
      if (!pointer || pointer.id !== event.pointerId) return;
      pointerRef.current = null;
      if (event.currentTarget.hasPointerCapture(event.pointerId)) {
        event.currentTarget.releasePointerCapture(event.pointerId);
      }
      if (pointer.moved) {
        positionRef.current = 'custom';
        setPosition('custom');
        suppressClickRef.current = true;
        window.requestAnimationFrame(() => {
          suppressClickRef.current = false;
        });
      }
      setDragging(false);
    },
    []
  );

  const moveByKey = useCallback(
    (key: 'ArrowUp' | 'ArrowDown' | 'ArrowLeft' | 'ArrowRight') => {
      const boundary = boundaryRef.current;
      const panel = panelRef.current;
      if (!boundary || !panel) return;
      const step = 24;
      const dx = key === 'ArrowLeft' ? -step : key === 'ArrowRight' ? step : 0;
      const dy = key === 'ArrowUp' ? -step : key === 'ArrowDown' ? step : 0;
      positionRef.current = 'custom';
      setPosition('custom');
      setPoint((current) =>
        clampFloatingVoiceDockPoint(
          { x: current.x + dx, y: current.y + dy },
          { width: boundary.clientWidth, height: boundary.clientHeight },
          { width: panel.offsetWidth, height: panel.offsetHeight }
        )
      );
    },
    []
  );

  return {
    boundaryRef,
    panelRef,
    position,
    point,
    ready,
    dragging,
    onDragPointerDown,
    onDragPointerMove,
    onDragPointerUp,
    moveByKey,
    shouldSuppressClick: () => suppressClickRef.current,
  };
}
